import { useCurrentCommunityContext } from "@circle-react/contexts/currentCommunityContext";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useSpaceResource } from "@circle-react/contexts/useSpaceResource";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { useUpdateCommunity } from "@circle-react/hooks/useUpdateCommunity";
import type { Space } from "@circle-react/types/Space";

import { isCommunityAdmin } from "@/react/helpers/communityMemberHelpers";
import { useSpaceApi } from "@/react/hooks/useSpaceApi";

export interface CoverImageFormValues {
  cover_image: string;
}

export const useEditHomeCover = () => {
  const { currentCommunityMember } = usePunditUserContext();
  const { mutateAsync: updateCommunity, isPending: isUpdating } =
    useUpdateCommunity();
  const {
    actions: { refresh },
    helpers: { isDataRefreshing },
    record: { home_page_cover_image_url: coverImageUrl },
  } = useCurrentCommunityContext();

  const onSubmit = (formValues: CoverImageFormValues) => {
    void updateCommunity({
      community: { home_page_cover_image: formValues.cover_image },
    });
  };

  const onDelete = async () => {
    await updateCommunity({
      community: { home_page_cover_image: null },
    });
    await refresh();
  };

  const isSmallScreen = useSmScreenMediaQuery();
  const coverImage = coverImageUrl || "";

  return {
    coverImage,
    hasCoverImage: Boolean(coverImage),
    isAdmin: isCommunityAdmin(currentCommunityMember),
    isLoading: isUpdating || Boolean(isDataRefreshing?.()),
    isSmallScreen,
    onDelete,
    onSubmit,
  };
};

export const useEditSpaceCover = (space: Space) => {
  const { id: spaceId } = space;
  const { updateSpace } = useSpaceApi();
  const { refetch, isPending } = useSpaceResource({ spaceId });

  const onSubmit = async ({ cover_image }: CoverImageFormValues) => {
    await updateSpace({ cover_image }, spaceId);
    await refetch();
  };

  const onDelete = async () => {
    await updateSpace(
      { cover_image: null, cover_image_visible: false },
      spaceId,
    );
    await refetch();
  };

  const {
    cover_image_url: coverImageUrl,
    cover_image_visible: isCoverImageVisible,
    thumbnail_image_url: thumbnailImageUrl,
  } = space;
  const isSmallScreen = useSmScreenMediaQuery();

  return {
    coverImage: coverImageUrl || "",
    isCoverImageVisible,
    isPending,
    isSmallScreen,
    onDelete,
    onSubmit,
    thumbnailImage: thumbnailImageUrl || "",
  };
};
